import * as THREE from 'three/webgpu';

import { RENDER_ORDER, missionMarkerMaterial } from './materials.js';

/**
 * @file `MissionMarkerBatch` — the glowing circle on the tarmac.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS A RING, NOT A DISC, AND NOT A PILLAR
 * ─────────────────────────────────────────────────────────────────────────────
 * The obvious GTA reading is a vertical column of light, and it is the wrong
 * shape for this game. At the fixed 52-degree pitch a column is a tall sliver
 * that occludes the very doorway it is marking, it needs its own sorting
 * against every building it stands beside, and it reads as a wall. What the
 * player actually has to know is WHERE THE EDGE IS — the circle they must step
 * inside — and that is a ring lying flat, which is also the shape the objective
 * radius literally is.
 *
 * So: one instanced quad per marker, `ShadowBlobs.js`'s deal again, with the
 * mask making a bright annulus at ~0.72 of the quad's half-width over a faint
 * interior wash. The wash is what stops the middle from reading as a hole.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PER-FRAME REBUILD, NOT A SLOT POOL
 * ─────────────────────────────────────────────────────────────────────────────
 * `reset()` → `add()`* → `commit(t)`, exactly like `render/LightPools.js` and
 * for the same reason: a marker set is a pure function of mission state the
 * director already owns, so re-deriving at most {@link MISSIONS.worldMarkerMax}
 * of them per frame is cheaper than keeping a free list honest across a mission
 * completing, failing and being offered again. There is no slot to leak.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **THE PULSE IS IN THE ALPHA, NEVER IN THE SCALE.** A ring that grows and
 *   shrinks is a ring whose EDGE moves, and the edge is the objective radius —
 *   a player who steps on a marker at its smallest and is not counted as
 *   arrived will call it a bug, and they will be right. The geometry is exactly
 *   `2 * r` across at all times and only the brightness breathes.
 *
 * • **IT IGNORES DEPTH** (`missionMarkerMaterial`), so it is drawn over
 *   everything before it in the frame. That is deliberate — see the material's
 *   note — but it means the batch must stay SMALL and NEAR: `Missions.js`
 *   caps the world set at 8 markers inside 140 m, because a ring on the far
 *   side of the island painted over the buildings in front of you is a smear,
 *   not a signpost.
 *
 * • **THE TINT IS LINEAR RGB AND ADDITIVE**, so the number that matters against
 *   the bloom threshold is `tint * alpha * opacity`, never the tint alone. Day
 *   bloom starts at 0.80 (`render/post.js`) — {@link MARKER}'s colours are
 *   picked to land just over it at the pulse peak on a lit road.
 *
 * @module engine/render/MissionMarkers
 */

/** @typedef {import('../types.js').Registry} Registry */

/** Ring geometry, colour and cadence. Metres, LINEAR RGB and seconds. */
export const MARKER = Object.freeze({
  /** Metres above the sampled ground. Vestigial (the quad ignores depth), but
   *  it keeps the geometry off the exact ground plane for free. */
  lift: 0.03,
  /**
   * The quad is this much wider than the objective radius, so the ring's SOFT
   * OUTER falloff has somewhere to live and the bright line still lands exactly
   * on `r`. Change this and {@link RING.mid} has to move with it.
   */
  pad: 1.32,
  /** Pulse rate, Hz. 0.75 is a slow breath — a fast blink reads as an alarm. */
  pulseHz: 0.75,
  /** Alpha at the bottom and the top of the breath. */
  pulseMin: 0.62,
  pulseMax: 1,
  /** A marker for a mission you cannot take right now (another one is running)
   *  is drawn at this share of full alpha — visible, obviously not for you. */
  dim: 0.34,
  /** The tracked mission's marker gets this much more, so one of several stands
   *  out without a second colour. */
  trackedBoost: 1.25,

  /** Per-kind linear tints. Additive: these ARE the peak the road gains. */
  tints: Object.freeze({
    /** A story mission's pickup: gold, the project's HUD accent. */
    giver: /** @type {[number, number, number]} */ ([0.62, 0.46, 0.13]),
    /** An optional one: cool steel, so the two read apart at a glance. */
    giver_optional: /** @type {[number, number, number]} */ ([0.24, 0.42, 0.58]),
    /** The live objective: near-white, the brightest thing on the ground. */
    objective: /** @type {[number, number, number]} */ ([0.52, 0.58, 0.62]),
  }),
});

/** The mask's shape, in normalised radius. */
const RING = Object.freeze({
  /** Where the bright line sits — `1 / MARKER.pad`, i.e. exactly at `r`. */
  mid: 1 / MARKER.pad,
  /** Half-width of the bright line. */
  half: 0.12,
  /** Flat wash inside the ring, so the disc is not a hole. */
  fill: 0.16,
});

export class MissionMarkerBatch {
  /**
   * @param {Object} [o]
   * @param {number} [o.max=16] Instance cap. `Missions.js` publishes at most 8;
   *   the slack is there so a future second marker source cannot silently drop
   *   rings.
   * @param {number} [o.size=96] Falloff texture resolution. Higher than the
   *   blob's 64 because a thin bright annulus bands where a radial smear does
   *   not.
   * @param {number} [o.opacity=1] Global strength — see the material.
   * @param {import('../types.js').Registry|null} [o.registry]
   * @param {string} [o.name='missionMarkers']
   */
  constructor(o = {}) {
    const {
      max = 16,
      size = 96,
      opacity = 1,
      registry = null,
      name = 'missionMarkers',
    } = o;

    this.max = Math.max(1, max | 0);
    /** @type {number} Instances written since the last {@link reset}. */
    this.count = 0;
    this._overflow = 0;
    this._disposed = false;

    /** @type {THREE.Texture} */
    this.texture = registry
      ? registry.texture(`${name}:ring`, () => makeRingTexture(size))
      : makeRingTexture(size);

    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this._tint = new THREE.InstancedBufferAttribute(new Float32Array(this.max * 3), 3);
    this._alpha = new THREE.InstancedBufferAttribute(new Float32Array(this.max), 1);
    geo.setAttribute('iTint', this._tint);
    geo.setAttribute('iAlpha', this._alpha);
    // The batch is never frustum-culled (see below), so the sphere only has to
    // be large enough that nothing else tries.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

    const mat = missionMarkerMaterial({ tex: this.texture, opacity, name });
    if (registry) registry.track?.(mat);
    /** @type {THREE.Material} */
    this.material = mat;

    /** @type {THREE.InstancedMesh} */
    this.object = new THREE.InstancedMesh(geo, mat, this.max);
    this.object.name = name;
    this.object.count = 0;
    this.object.renderOrder = RENDER_ORDER.marker;
    // A handful of instances that move whenever a mission does; a correct
    // bounding sphere would have to be rebuilt every frame to save one draw.
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();
    // `InstancedMesh` fills its matrices with IDENTITY, which is a 1x1 quad at
    // the origin — untouched slots must draw nothing.
    this.object.instanceMatrix.array.fill(0);

    this._m = new THREE.Matrix4();
  }

  /** Starts a frame. Everything not re-added between here and {@link commit}
   *  stops being drawn. */
  reset() {
    this.count = 0;
    return this;
  }

  /**
   * One marker.
   *
   * @param {number} x @param {number} y Ground height — `MARKER.lift` is added
   *   for you. @param {number} z
   * @param {number} r The OBJECTIVE RADIUS in metres. The bright line lands
   *   exactly here; the quad is drawn `MARKER.pad` wider so the falloff fits.
   * @param {[number, number, number]|null} [tint] Linear RGB. Defaults to the
   *   objective colour.
   * @param {number} [alpha=1] Per-instance strength before the pulse.
   * @returns {boolean} False when the batch is full (counted, never grown).
   */
  add(x, y, z, r, tint = null, alpha = 1) {
    if (this._disposed) return false;
    if (this.count >= this.max) {
      if (this._overflow++ === 0) {
        console.warn(`[MissionMarkerBatch] full at ${this.max} markers; extras dropped`);
      }
      return false;
    }
    const i = this.count++;
    const d = Math.max(0.5, r) * 2 * MARKER.pad;
    this._m.makeScale(d, 1, d);
    this._m.elements[12] = x;
    this._m.elements[13] = y + MARKER.lift;
    this._m.elements[14] = z;
    this.object.setMatrixAt(i, this._m);

    const c = tint ?? MARKER.tints.objective;
    const o = i * 3;
    this._tint.array[o] = c[0];
    this._tint.array[o + 1] = c[1];
    this._tint.array[o + 2] = c[2];
    this._alpha.array[i] = alpha < 0 ? 0 : alpha;
    return true;
  }

  /**
   * Uploads the frame and applies the breath.
   *
   * The pulse is a single global multiplier rather than a per-marker phase on
   * purpose: two rings in view breathing out of step read as two unrelated
   * effects, where one shared beat reads as the HUD.
   *
   * @param {number} t Seconds. Any monotonic clock — the level's is fine.
   */
  commit(t = 0) {
    if (this._disposed) return;
    const w = 0.5 - 0.5 * Math.cos(t * MARKER.pulseHz * Math.PI * 2);
    const pulse = MARKER.pulseMin + (MARKER.pulseMax - MARKER.pulseMin) * w;
    for (let i = 0; i < this.count; i++) this._alpha.array[i] *= pulse;
    this.object.instanceMatrix.needsUpdate = true;
    this._tint.needsUpdate = true;
    this._alpha.needsUpdate = true;
    this.object.count = this.count;
    this.object.visible = this.count > 0;
  }

  /** @returns {{count: number, max: number, dropped: number}} */
  stats() {
    return { count: this.count, max: this.max, dropped: this._overflow };
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.object.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/**
 * The ring mask, in the ALPHA channel (all the material reads).
 *
 * `alpha = ring(d) + fill(d)`, where `ring` is a smooth band centred on
 * {@link RING.mid} and `fill` is a soft wash that dies out at the same place.
 * Both are eased twice, because a thin bright annulus in an 8-bit alpha channel
 * BANDS visibly at this scale where the blob shadow's broad smear does not.
 *
 * Built arithmetically rather than through a canvas gradient — same reasoning
 * as `ShadowBlobs.js#makeFalloffTexture`: this class then constructs in node,
 * which is what lets a headless test build one.
 *
 * @param {number} size Texture edge, pixels.
 * @returns {THREE.DataTexture}
 */
export function makeRingTexture(size = 96) {
  const n = Math.max(8, size | 0);
  const data = new Uint8Array(n * n * 4);
  const c = (n - 1) / 2;

  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = (x - c) / (n / 2);
      const dy = (y - c) / (n / 2);
      const d = Math.hypot(dx, dy);

      // The bright line: 1 on the centre-line, 0 at `half` either side.
      let ring = 1 - Math.min(1, Math.abs(d - RING.mid) / RING.half);
      ring *= ring * (3 - 2 * ring);

      // The interior wash: full at the middle, gone by the ring.
      const fill = d >= RING.mid ? 0 : RING.fill * (1 - smoothstep(RING.mid * 0.35, RING.mid, d));

      // Hard cut past the quad's inscribed circle, or the ring's outer falloff
      // would be clipped square by the quad's own corners.
      const a = d > 1 ? 0 : Math.min(1, ring + fill);
      const i = (y * n + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(a * 255);
    }
  }

  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  tex.name = 'missionRing';
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/** @param {number} a @param {number} b @param {number} x @returns {number} 0..1 */
function smoothstep(a, b, x) {
  const t = b === a ? 0 : (x - a) / (b - a);
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return k * k * (3 - 2 * k);
}
